import { lazy, Suspense, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

/// Components
import Index from "./jsx";
import { useBranding } from "./context/BrandingContext";
import { connect, useDispatch } from 'react-redux';
import { Route, Routes, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';

// action
import { checkAuthStatus } from './store/actions/AuthActions';
import { fetchMySubscription } from './store/actions/SubscriptionActions';
import { isAuthenticated } from './store/selectors/AuthSelectors';

/// Style
import "./vendor/bootstrap-select/dist/css/bootstrap-select.min.css";
import "./css/style.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./theme/console.css";
import "./css/subscription-styles.css";


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const StreamLogin = lazy(() => import('./jsx/pages/StreamLogin'));
const SignUp = lazy(() => import('./jsx/pages/Registration'));
const Login = lazy(() => {
  return new Promise(resolve => {
    setTimeout(() => resolve(import('./jsx/pages/Login')), 500);
  });
});
const EmailVerification = lazy(() => import('./jsx/pages/EmailVerification'));
const PhoneVerification = lazy(() => import('./jsx/pages/PhoneVerification'));


const Loader = () => (
  <div id="preloader">
    <div className="sk-three-bounce">
      <div className="sk-child sk-bounce1"></div>
      <div className="sk-child sk-bounce2"></div>
      <div className="sk-child sk-bounce3"></div>
    </div>
  </div>
);

function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    let location = useLocation();
    let navigate = useNavigate();
    let params = useParams();
    return (
      <Component
        {...props}
        router={{ location, navigate, params }}
      />
    );
  }
  return ComponentWithRouterProp;
}

// Check if current user has an active subscription (from localStorage)
function getLocalSubscriptionStatus() {
  try {
    const user = JSON.parse(localStorage.getItem('streamUser') || '{}');
    // Admins always bypass subscription gate; team members never see
    // subscription-gated content at all.
    if (user.role === 'admin' || user.role === 'team_member') return true;
    // Check stored subscription flag
    return localStorage.getItem('hasSubscription') === 'true';
  } catch {
    return false;
  }
}

function App(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signupAllowed } = useBranding();
  // Track subscription status — start from localStorage, then verify from API
  const [hasSubscription, setHasSubscription] = useState(getLocalSubscriptionStatus());
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  useEffect(() => {
    checkAuthStatus()(dispatch);
  }, []);

  // Function to check subscription status - can be called from outside
  const checkSubscriptionStatus = useCallback(async (retryCount = 0, maxRetries = 3) => {
    const token = localStorage.getItem('streamToken');
    const user = (() => {
      try { return JSON.parse(localStorage.getItem('streamUser') || '{}'); } catch { return {}; }
    })();

    // Admins always have access; team members never reach subscription-gated
    // content at all (Markup shows them a mobile-only notice instead), so
    // there's nothing to check for them either.
    if (user.role === 'admin' || user.role === 'team_member') {
      setHasSubscription(true);
      setSubscriptionChecked(true);
      localStorage.setItem('hasSubscription', 'true');
      return true;
    }

    try {
      const res = await axios.get(`${API_URL}/subscription/check-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = res.data || {};
      const subscriptionStatus = (
        data?.subscription?.status ||
        data?.status ||
        data?.subscription_status ||
        ''
      ).toString().toLowerCase();

      const activeStatuses = new Set([
        'active',
        'trialing',
        'paid',
        'past_due'
      ]);

      const active =
        data?.has_active_subscription === true ||
        data?.hasSubscription === true ||
        data?.active === true ||
        activeStatuses.has(subscriptionStatus);

      setHasSubscription(active);
      localStorage.setItem('hasSubscription', active ? 'true' : 'false');
      setSubscriptionChecked(true);
      return active;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      
      // Retry logic
      if (retryCount < maxRetries) {
        console.log(`Retrying subscription check (${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return checkSubscriptionStatus(retryCount + 1, maxRetries);
      }
      
      // On error after retries, fall back to localStorage value
      const localStatus = getLocalSubscriptionStatus();
      setHasSubscription(localStatus);
      setSubscriptionChecked(true);
      return localStatus;
    }
  }, []);

  // Expose subscription check function globally for external components
  useEffect(() => {
    window.refreshSubscriptionStatus = checkSubscriptionStatus;
    return () => {
      delete window.refreshSubscriptionStatus;
    };
  }, [checkSubscriptionStatus]);

  // When user becomes authenticated, verify subscription status from API
  useEffect(() => {

    if (!props.isAuthenticated) {
      setSubscriptionChecked(false);
      return;
    }

    const token = localStorage.getItem('streamToken');
    const user = (() => {
      try { return JSON.parse(localStorage.getItem('streamUser') || '{}'); } catch { return {}; }
    })();

    // Admins always have access — no API call needed; team members never see
    // subscription-gated content (Markup replaces the whole console for them).
    if (user.role === 'admin' || user.role === 'team_member') {
      setHasSubscription(true);
      setSubscriptionChecked(true);
      return;
    }

    // Fetch full subscription details for the header widget (shows plan name, days left, etc.)
    dispatch(fetchMySubscription());

    // Verify subscription status from API with retry logic
    checkSubscriptionStatus();
  }, [props.isAuthenticated, dispatch, checkSubscriptionStatus]);



  // Account creation is only available on the platform's own hosts — on a
  // white-label custom domain the sign-up routes bounce to login.
  const signupEl = signupAllowed ? <SignUp /> : <Navigate to="/login" replace />;
  const publicRoutes = (
    <Routes>
      <Route path='/' element={<Navigate to="/login" replace />} />
      <Route path='/login' element={<StreamLogin />} />
      <Route path='/register' element={signupEl} />
      <Route path='/page-register' element={signupEl} />
      <Route path='/page-login' element={<Login />} />
      <Route path='/verify-email' element={<EmailVerification />} />
      <Route path='/verify-phone' element={<PhoneVerification />} />
      <Route path='*' element={<Navigate to="/login" replace />} />
    </Routes>
  );


  if (props.isAuthenticated) {
    // Show loader while verifying subscription from API
    if (!subscriptionChecked) {
      return <Loader />;
    }

    return (
      <Suspense fallback={<Loader />}>
        <Index subscriptionGated={!hasSubscription} />
      </Suspense>
    );
  }

  return (
    <div className="vh-100">
      <Suspense fallback={<Loader />}>
        {publicRoutes}
      </Suspense>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    isAuthenticated: isAuthenticated(state),
  };
};

export default withRouter(connect(mapStateToProps)(App));
